import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useProducts, useProductMutations } from '../api';
import { PageLoader, ErrorState, Pagination, StatusBadge, EmptyState } from '../../components/ui';
import { money, weight, num } from '../../lib/format';
import { ProductThumb } from '../../visitor/ProductThumb';
import { ProductEditModal } from '../ProductEditModal';
import { ProductCreateModal } from '../ProductCreateModal';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useToast } from '../../components/Toast';
import { ApiRequestError } from '../../lib/api';
import type { Product } from '../../lib/types';

export function ProductsPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [sheet, setSheet] = useState('');
  const [hasImage, setHasImage] = useState('');

  const { data, isLoading, error, refetch, isFetching } = useProducts({
    page,
    limit: 25,
    search: search || undefined,
    status: status || undefined,
    sourceSheet: sheet || undefined,
    hasImage: hasImage || undefined,
  });
  const { remove, bulkDelete } = useProductMutations();
  const toast = useToast();

  const rows = useMemo(() => data?.data ?? [], [data]);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<Product | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<Product | null>(null);
  const [deleteForce, setDeleteForce] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForce, setBulkForce] = useState(false);

  const pageIds = rows.map((r) => r._id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };
  const toggleOne = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const doDelete = async () => {
    if (!deleting) return;
    try {
      await remove.mutateAsync({ id: deleting._id, force: deleteForce });
      toast(`Deleted ${deleting.barcodeNo}`, 'success');
      setSelected((s) => {
        const n = new Set(s);
        n.delete(deleting._id);
        return n;
      });
      setDeleting(null);
      setDeleteForce(false);
    } catch (e) {
      const err = e as ApiRequestError;
      if (err.code === 'HAS_CHILDREN' && !deleteForce) {
        setDeleteForce(true);
        toast('Product is assigned to an expo — confirm force delete', 'info');
        return;
      }
      if (err.code === 'HAS_HISTORY') {
        toast('In historical bookings — set status to "archived" instead.', 'error');
        setDeleting(null);
        return;
      }
      toast(err.message ?? 'Delete failed', 'error');
    }
  };

  const doBulkDelete = async () => {
    const ids = [...selected];
    try {
      const res = await bulkDelete.mutateAsync({ ids, force: bulkForce });
      const okN = res.deleted.length;
      const skipN = res.skipped.length;
      toast(
        skipN
          ? `Deleted ${okN}. Skipped ${skipN} (${[...new Set(res.skipped.map((s) => s.reason))].join(', ')})`
          : `Deleted ${okN} product${okN === 1 ? '' : 's'}`,
        skipN ? 'info' : 'success',
      );
      setSelected(new Set(res.skipped.map((s) => s.id)));
      setBulkOpen(false);
      setBulkForce(false);
    } catch (e) {
      toast(e instanceof ApiRequestError ? e.message : 'Bulk delete failed', 'error');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-ink-900">Jewellery</h1>
          <p className="text-sm text-ink-400">
            {data?.meta ? `${num(data.meta.total)} items in the product master` : ' '}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link className="btn-outline" to="/admin/import">
            Import Excel
          </Link>
          <button className="btn-primary" onClick={() => setCreating(true)}>
            Add jewellery
          </button>
        </div>
      </div>

      <div className="card flex flex-wrap gap-2 p-3">
        <input
          className="input max-w-xs"
          placeholder="Search barcode / Photo ID / name…"
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
        />
        <select className="input w-auto" value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }}>
          <option value="">All status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="archived">Archived</option>
        </select>
        <select className="input w-auto" value={sheet} onChange={(e) => { setPage(1); setSheet(e.target.value); }}>
          <option value="">Any sheet</option>
          <option value="Master">Master</option>
          <option value="Master1">Master1</option>
        </select>
        <select className="input w-auto" value={hasImage} onChange={(e) => { setPage(1); setHasImage(e.target.value); }}>
          <option value="">Photo: any</option>
          <option value="true">Has photo</option>
          <option value="false">No photo</option>
        </select>
      </div>

      {/* bulk action bar */}
      {selected.size > 0 && (
        <div className="flex items-center justify-between rounded-lg border border-ink-900 bg-ink-900 px-4 py-2 text-sm text-white">
          <span>{selected.size} selected</span>
          <div className="flex items-center gap-2">
            <button className="btn-ghost text-white hover:bg-ink-800" onClick={() => setSelected(new Set())}>
              Clear
            </button>
            <button className="btn-danger" onClick={() => { setBulkForce(false); setBulkOpen(true); }}>
              Delete selected
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <PageLoader />
      ) : error ? (
        <ErrorState message={(error as Error).message} onRetry={() => refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState title="No products match" hint="Adjust the filters or import the Excel file." />
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="table-head">
                  <th className="px-3 py-2">
                    <input type="checkbox" checked={allOnPageSelected} onChange={toggleAll} aria-label="Select all" />
                  </th>
                  <th className="px-3 py-2">Photo</th>
                  <th className="px-3 py-2">Barcode</th>
                  <th className="px-3 py-2">Photo ID</th>
                  <th className="px-3 py-2">Size</th>
                  <th className="px-3 py-2 text-right">Gwt</th>
                  <th className="px-3 py-2 text-right">Swt</th>
                  <th className="px-3 py-2 text-right">Stone Amt</th>
                  <th className="px-3 py-2 text-right">Price</th>
                  <th className="px-3 py-2">Sheet</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-50">
                {rows.map((p) => (
                  <tr key={p._id} className={selected.has(p._id) ? 'bg-gold-50/60' : 'hover:bg-ink-50/60'}>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selected.has(p._id)}
                        onChange={() => toggleOne(p._id)}
                        aria-label={`Select ${p.barcodeNo}`}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <div className="h-10 w-10 overflow-hidden rounded-md bg-ink-50">
                        <ProductThumb src={p.primaryImage?.thumbnailUrl ?? p.images?.[0]?.thumbnailUrl} alt={p.barcodeNo} />
                      </div>
                    </td>
                    <td className="px-3 py-2 font-mono">
                      <Link className="hover:underline" to={`/admin/jewellery/${p._id}`}>
                        {p.barcodeNo}
                      </Link>
                    </td>
                    <td className="px-3 py-2">{p.photoId ?? '—'}</td>
                    <td className="px-3 py-2">{p.size ?? '—'}</td>
                    <td className="px-3 py-2 text-right">{weight(p.grossWeight)}</td>
                    <td className="px-3 py-2 text-right">{p.stoneWeight ? weight(p.stoneWeight) : '—'}</td>
                    <td className="px-3 py-2 text-right">{p.stoneAmount ? money(p.stoneAmount, true) : '—'}</td>
                    <td className="px-3 py-2 text-right">{money(p.displayPrice ?? p.sellingPrice)}</td>
                    <td className="px-3 py-2 text-xs text-ink-400">{p.originalImport?.sourceSheet ?? 'manual'}</td>
                    <td className="px-3 py-2">
                      <StatusBadge status={p.status} />
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-1">
                        <button className="btn-ghost px-2 py-1" onClick={() => setEditing(p)}>
                          Edit
                        </button>
                        <button
                          className="btn-ghost px-2 py-1 text-red-600 hover:bg-red-50"
                          onClick={() => {
                            setDeleteForce(false);
                            setDeleting(p);
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-3">
            <Pagination page={page} pages={data?.meta?.pages ?? 1} onPage={setPage} />
          </div>
          {isFetching && <div className="px-3 pb-2 text-xs text-ink-300">Updating…</div>}
        </div>
      )}

      <ProductCreateModal open={creating} onClose={() => setCreating(false)} onSaved={() => refetch()} />

      <ProductEditModal product={editing} open={!!editing} onClose={() => setEditing(null)} onSaved={() => refetch()} />

      <ConfirmDialog
        open={!!deleting}
        title={deleteForce ? 'Force delete product?' : 'Delete product?'}
        tone="danger"
        busy={remove.isPending}
        confirmLabel={deleteForce ? 'Force delete' : 'Delete'}
        message={
          deleting && (
            <>
              {deleteForce ? (
                <p>
                  <b>{deleting.barcodeNo}</b> is assigned to one or more expos. Force-deleting will
                  remove those assignments too. This cannot be undone.
                </p>
              ) : (
                <p>
                  Permanently delete <b>{deleting.barcodeNo}</b>
                  {deleting.photoId ? ` — ${deleting.photoId}` : ''}? Products used in historical
                  bookings cannot be deleted.
                </p>
              )}
            </>
          )
        }
        onCancel={() => {
          setDeleting(null);
          setDeleteForce(false);
        }}
        onConfirm={doDelete}
      />

      <ConfirmDialog
        open={bulkOpen}
        title={`Delete ${selected.size} product${selected.size === 1 ? '' : 's'}?`}
        tone="danger"
        busy={bulkDelete.isPending}
        confirmLabel="Delete selected"
        message={
          <div className="space-y-2">
            <p>This permanently removes the selected products. Items in historical bookings are skipped automatically.</p>
            <label className="flex items-center gap-2 text-xs text-ink-600">
              <input type="checkbox" checked={bulkForce} onChange={(e) => setBulkForce(e.target.checked)} />
              Also unassign & delete products currently assigned to an expo
            </label>
          </div>
        }
        onCancel={() => setBulkOpen(false)}
        onConfirm={doBulkDelete}
      />
    </div>
  );
}
